import type {
  ChangeTicketClaimStatusRequestBodyDTO,
  ChangeTicketDetailsRequestBodyDTO,
  ChangeTicketPriorityRequestBodyDTO,
  ChangeTicketsReadStatusRequestBodyDTO,
  CloseTicketRequestBodyDTO,
  CreateMultipleTicketsRequestBodyDTO,
  CreateTicketAttachmentRequestBodyDTO,
  CreateTicketMessageRequestBodyDTO,
  CreateTicketMultiPartAttachmentCompleteRequestBodyDTO,
  CreateTicketMultiPartAttachmentInitiateRequestBodyDTO,
  CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
  CreateTicketMultiPartAttachmentUploadRequestBodyDTO,
  CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
  CreateTicketRequestBodyDTO,
  CreateTicketScreenshotMessageRequestBodyDTO,
  ExportTicketsRequestDTO,
  GetApprovalTicketRequestBodyDTO,
  GetRoofTopsListResponseDTO,
  GetScheduleFilesResponseDTO,
  GetTicketAttachmentsResponseDTO,
  GetTicketMessagesResponseDTO,
  GetTicketResponseDTO,
  GetTicketsCountByRoofTopResponseDTO,
  GetTicketsCountByRooftopsParamsDTO,
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
  GetTicketsTotalsByRoofTopResponseDTO,
  ImportScheduleRequestParamsDTO,
  ImportScheduleResponseDTO,
  ReassignTicketsRequestBodyDTO,
  ReopenTicketRequestBodyDTO,
  ShareTicketsRequestBodyDTO,
} from 'declarations/dto/tickets';

import { get, post, put, remove } from './api';

export const getTicketsApi = async (
  params?: GetTicketsRequestParamsDTO
): Promise<GetTicketsResponseDTO> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO params are serializable by Axios
  const response = await get<GetTicketsResponseDTO>('/tickets', params as any);

  return response.data;
};

export const getTicketApi = async (ticketId: string): Promise<GetTicketResponseDTO> => {
  const response = await get<GetTicketResponseDTO>(`/tickets/${ticketId}/details`);

  return response.data;
};

export const fetchTicketsCountByRoofTopApi = async (
  params: GetTicketsCountByRooftopsParamsDTO
): Promise<GetTicketsCountByRoofTopResponseDTO> => {
  const response = await post<GetTicketsCountByRoofTopResponseDTO>(
    '/tickets/count-by-rooftop',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO params are serializable by Axios
    params as any
  );

  return response.data;
};

export const fetchTicketsTotalsByRoofTopApi = async (
  params: GetTicketsCountByRooftopsParamsDTO
): Promise<GetTicketsTotalsByRoofTopResponseDTO> => {
  const response = await post<GetTicketsTotalsByRoofTopResponseDTO>(
    '/tickets/totals-by-rooftop',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO params are serializable by Axios
    params as any
  );

  return response.data;
};

export const getRoofTopsListApi = async (): Promise<GetRoofTopsListResponseDTO> => {
  const response = await get<GetRoofTopsListResponseDTO>('/users/current-user/mapped-rooftops');

  return response.data;
};

export const createTicketApi = async (data: CreateTicketRequestBodyDTO): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<void>('/tickets', data as any);

  return response.data;
};

export const createMultipleTicketsApi = async (
  data: CreateMultipleTicketsRequestBodyDTO
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<void>('/tickets/bulk', data as any);

  return response.data;
};

export const reopenTicketApi = async (data: ReopenTicketRequestBodyDTO): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<void>('/tickets/reopen', data as any);

  return response.data;
};

export const getApprovalTicketApi = async (
  data: GetApprovalTicketRequestBodyDTO
): Promise<void> => {
  const response = await post<void>('/tickets/get-approval', data);

  return response.data;
};

export const closeTicketApi = async (data: CloseTicketRequestBodyDTO): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<void>('/tickets/close', data as any);

  return response.data;
};

export const createTicketMessageApi = async (
  ticketId: string,
  data: CreateTicketMessageRequestBodyDTO
): Promise<void> => {
  const response = await post<void>(`/tickets/${ticketId}/text-messages`, data);

  return response.data;
};

export const createScreenshotMessageApi = async (
  ticketId: string,
  data: CreateTicketScreenshotMessageRequestBodyDTO
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FormData is serializable by Axios
  const response = await post<void>(`/tickets/${ticketId}/screenshots`, data as any, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

export const createTicketAttachmentApi = async (
  ticketId: string,
  data: CreateTicketAttachmentRequestBodyDTO
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FormData is serializable by Axios
  const response = await post<void>(`/tickets/${ticketId}/attachments`, data as any, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

export const createTicketMultiAttachmentApi = async (
  ticketId: string,
  data: CreateTicketAttachmentRequestBodyDTO[]
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FormData array is serializable by Axios
  const response = await post<void>(`/tickets/${ticketId}/attachments`, data as any, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

export const getTicketAttachmentsApi = async (
  ticketId: string
): Promise<GetTicketAttachmentsResponseDTO> => {
  const response = await get<GetTicketAttachmentsResponseDTO>(`/tickets/${ticketId}/attachments`);

  return response.data;
};

export const getTicketMessagesApi = async (
  ticketId: string
): Promise<GetTicketMessagesResponseDTO> => {
  const response = await get<GetTicketMessagesResponseDTO>(`/tickets/${ticketId}/messages`);

  return response.data;
};

export const changeTicketClaimStatusApi = async (
  data: ChangeTicketClaimStatusRequestBodyDTO
): Promise<void> => {
  const response = await put<void>(
    `/tickets/${data.ticketId}/claim-status/${data.claimStatusId}`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
    data as any
  );

  return response.data;
};

export const changeTicketPriorityApi = async (
  data: ChangeTicketPriorityRequestBodyDTO
): Promise<void> => {
  const response = await put<void>(
    `/tickets/${data.ticketId}/priorities/${data.ticketPriorityId}`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
    data as any
  );

  return response.data;
};

export const changeTicketDetailsApi = async (
  data: ChangeTicketDetailsRequestBodyDTO
): Promise<void> => {
  const response = await put<void>(`/tickets/${data.ticketId}`, data);

  return response.data;
};

export const markTicketsAsReadApi = async (
  data: ChangeTicketsReadStatusRequestBodyDTO
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<void>('/tickets/mark-read', data as any);

  return response.data;
};

export const markTicketsAsUnReadApi = async (
  data: ChangeTicketsReadStatusRequestBodyDTO
): Promise<void> => {
  const response = await post<void>('/tickets/mark-unread', data);

  return response.data;
};

export const deleteTicketApi = async (ticketId: string): Promise<void> => {
  const response = await remove<void>(`/tickets/${ticketId}`);

  return response.data;
};

export const createTicketMultiPartAttachmentInitiateApi = async (
  ticketId: string,
  data: CreateTicketMultiPartAttachmentInitiateRequestBodyDTO
): Promise<CreateTicketMultiPartAttachmentInitiateResponseBodyDTO> => {
  const response = await post<CreateTicketMultiPartAttachmentInitiateResponseBodyDTO>(
    `/tickets/${ticketId}/attachments/multi-part/initiate`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
    data as any
  );

  return response.data;
};

export const createTicketMultiPartAttachmentUploadApi = async (
  data: CreateTicketMultiPartAttachmentUploadRequestBodyDTO
): Promise<CreateTicketMultiPartAttachmentUploadResponseBodyDTO> => {
  const response = await post<CreateTicketMultiPartAttachmentUploadResponseBodyDTO>(
    '/tickets/attachments/multi-part/upload',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
    data as any
  );

  return response.data;
};

export const createTicketMultiPartAttachmentCompleteApi = async (
  ticketId: string,
  data: CreateTicketMultiPartAttachmentCompleteRequestBodyDTO
): Promise<void> => {
  const response = await post<void>(
    `/tickets/${ticketId}/attachments/multi-part/complete`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
    data as any
  );

  return response.data;
};

export const reassignTicketsApi = async (data: ReassignTicketsRequestBodyDTO): Promise<void> => {
  const response = await post<void>('/tickets/reassign', data);

  return response.data;
};

export const shareTicketsApi = async (data: ShareTicketsRequestBodyDTO): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<void>('/tickets/share', data as any);

  return response.data;
};

export const exportTicketsApi = async (body: ExportTicketsRequestDTO): Promise<Blob> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DTO body is serializable by Axios
  const response = await post<Blob>('/tickets/export', body as any, {
    responseType: 'blob',
  });

  return response.data;
};

export const importScheduleApi = async (
  params?: ImportScheduleRequestParamsDTO
): Promise<ImportScheduleResponseDTO> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FormData params are serializable by Axios
  const response = await post<ImportScheduleResponseDTO>('/tickets/schedule', params as any, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

export const getScheduleFilesApi = async (
  rooftopId: string
): Promise<GetScheduleFilesResponseDTO> => {
  const response = await get<GetScheduleFilesResponseDTO>(
    `/schedule-files/by-rooftop/${rooftopId}`
  );

  return response.data;
};
type AdvisorTechNumberItemT = {
  number: string;
  extraString1?: string;
};

export const getAdvisorNumberListApi = async (
  roofTopId: string
): Promise<AdvisorTechNumberItemT[]> => {
  const response = await get<AdvisorTechNumberItemT[]>(
    `/code-translations/by-work-item/${roofTopId}/advisor-numbers`
  );

  return response.data;
};
export const getTechNumberListApi = async (
  roofTopId: string
): Promise<AdvisorTechNumberItemT[]> => {
  const response = await get<AdvisorTechNumberItemT[]>(
    `/code-translations/by-work-item/${roofTopId}/tech-numbers`
  );

  return response.data;
};
